import type * as rest from "./RESTTypes";
import type { RESTRequest } from "./types";

export class RESTProjectApi {
  private baseUrl = "/beta";
  constructor(private request: RESTRequest) {}
  getBranchById(id: string) {
    return this.request<{ data: rest.BranchWithEnvironment }>({
      method: "GET",
      path: this.baseUrl + "/sandboxes/branches/" + id,
    }).then(({ data }) => data);
  }
  /**
   * @deprecated
   */
  getBranch({
    owner,
    repo,
    branch,
  }: {
    owner: string;
    repo: string;
    branch: string;
  }) {
    return this.request<{ data: rest.Branch }>({
      method: "GET",
      path: this.baseUrl + `/github/${owner}/${repo}/${branch}`,
    }).then(({ data }) => data);
  }
  /**
   * @deprecated
   */
  import({
    owner,
    repo,
    branch,
    teamId,
  }: {
    owner: string;
    repo: string;
    branch?: string;
    teamId?: string;
  }) {
    return this.request<{ data: rest.BranchImport }>({
      method: "POST",
      path:
        this.baseUrl + `/github/${owner}/${repo}${branch ? "/" + branch : ""}`,
      data: {
        teamId,
      },
    }).then(({ data }) => data);
  }
  forkRepository({
    owner,
    repo,
    branch,
    teamId,
    name,
    organization,
  }: {
    owner: string;
    repo: string;
    branch?: string;
    teamId?: string;
    name?: string;
    organization?: string;
  }) {
    return this.request<{ data: rest.BranchImport }>({
      method: "POST",
      path:
        this.baseUrl +
        `/fork/github/${owner}/${repo}${branch ? "/" + branch : ""}`,
      data: {
        teamId,
        name,
        organization,
      },
    }).then(({ data }) => data);
  }
  /**
   * Creates a branch on a repo. By default it creates
   * a branch from the default branch, unless `sourceBranch` is passed.
   * By default it creates the new branch name, unless `newBranch` is passed.
   */
  createBranch({
    owner,
    repo,
    sourceBranch,
    newBranch,
    teamId,
  }: {
    owner: string;
    repo: string;
    sourceBranch?: string;
    newBranch?: string;
    teamId?: string;
  }) {
    return this.request<{ data: rest.BranchImport }>({
      method: "POST",
      path: this.baseUrl + `/github/${owner}/${repo}`,
      data: {
        sourceBranch,
        newBranch,
        teamId,
      },
    }).then(({ data }) => data);
  }
  /**
   * Updates details of a branch
   */
  updateBranch(id: string, { name }: { name: string }) {
    return this.request<{ data: rest.Branch }>({
      method: "PATCH",
      path: this.baseUrl + `/sandboxes/branches/` + id,
      data: {
        name,
      },
    }).then(({ data }) => data);
  }
}

export class RESTSandboxesApi {
  private baseUrl = "/sandboxes";
  constructor(private request: RESTRequest) {}
  /**
   * Returns a Sandbox with modules and directories
   */
  get(id: string) {
    return this.request<{ data: rest.Sandbox }>({
      method: "GET",
      path: this.baseUrl + "/" + id,
    }).then(({ data }) => data);
  }
  update(id: string, data: { title: string }) {
    return this.request({
      method: "PATCH",
      path: this.baseUrl + "/" + id,
      data,
    });
  }
  fork(sandboxUrl: string) {
    return this.request({
      method: "POST",
      path: this.baseUrl + "/fork/github/" + sandboxUrl,
      data: {},
    });
  }
  getGitHubSandbox(sandboxUrl: string) {
    return this.request({
      method: "GET",
      path: this.baseUrl + "/github/" + sandboxUrl,
    });
  }
  createDefine() {
    return this.request({
      method: "POST",
      path: this.baseUrl + "/define",
      data: {},
    });
  }
  getDefine() {
    return this.request({
      method: "GET",
      path: this.baseUrl + "/define",
    });
  }
  createMslim() {
    return this.request({
      method: "POST",
      path: this.baseUrl + "/mslim",
      data: {},
    });
  }
  getPopular() {
    return this.request({
      method: "GET",
      path: this.baseUrl + "/popular",
    });
  }
  getPicked() {
    return this.request({
      method: "GET",
      path: this.baseUrl + "/picked",
    });
  }
  getLimits() {
    return this.request({
      method: "GET",
      path: this.baseUrl + "/limits",
    });
  }
  getExploreTemplates() {
    return this.request({
      method: "GET",
      path: this.baseUrl + "/templates/explore",
    });
  }
  getOfficialTemplates() {
    return this.request({
      method: "GET",
      path: this.baseUrl + "/templates/official",
    });
  }
}

export class RESTUsersApi {
  constructor(private request: RESTRequest) {}
  private baseUrl = "/users";
  createSurveyAnswer() {
    return this.request({
      method: "POST",
      path: this.baseUrl + "/survey-answered",
      data: {},
    });
  }
  flagSurveySeen() {
    return this.request({
      method: "POST",
      path: this.baseUrl + "/survey-seen",
      data: {},
    });
  }
  createExperiment() {
    return this.request({
      method: "POST",
      path: this.baseUrl + "/experiments",
      data: {},
    });
  }
  search() {
    return this.request({
      method: "GET",
      path: this.baseUrl + "/search",
    });
  }
  getPending(id: string) {
    return this.request({
      method: "GET",
      path: this.baseUrl + "/pending/" + id,
    });
  }
  getUsernameAvailability(username: string) {
    return this.request({
      method: "GET",
      path: this.baseUrl + "/available/" + username,
    });
  }
  finalize() {
    return this.request({
      method: "POST",
      path: this.baseUrl + "/finalize",
      data: {},
    });
  }
  deleteCurator(username: string) {
    return this.request({
      method: "DELETE",
      path: this.baseUrl + "/curators/" + username,
    });
  }
  createCurator(username: string) {
    return this.request({
      method: "POST",
      path: this.baseUrl + "/curators/" + username,
      data: {},
    });
  }
  getUserSandboxes(id: string) {
    return this.request({
      method: "GET",
      path: this.baseUrl + "/" + id + "/sandboxes",
    });
  }
  getUserLikedSandboxes(id: string) {
    return this.request({
      method: "GET",
      path: this.baseUrl + "/" + id + "/sandboxes/liked",
    });
  }
}

export class RESTCurrentUserApi {
  private baseUrl = "/users";
  constructor(private request: RESTRequest) {}
  getIntegrations() {
    return this.request({
      method: "GET",
      path: this.baseUrl + "/current/integrations",
    });
  }
  removeIntegration(integration: "zeit" | "github") {
    return this.request({
      method: "DELETE",
      path: this.baseUrl + "/current_user/integrations/" + integration,
    });
  }
  addIntegration(integration: { type: "zeit"; data: Record<string, unknown> }) {
    return this.request({
      method: "POST",
      path: this.baseUrl + "/current_user/integrations/" + integration.type,
      data: integration.data,
    });
  }
  getEditorSettings() {
    return this.request({
      method: "GET",
      path: this.baseUrl + "/current_user/editor_settings",
    });
  }
  setEditorSettings() {
    return this.request({
      method: "POST",
      path: this.baseUrl + "/current_user/editor_settings",
      data: {},
    });
  }
  unsetEditorSettings() {
    return this.request({
      method: "DELETE",
      path: this.baseUrl + "/current_user/editor_settings",
    });
  }
  updateEditorSettings() {
    return this.request({
      method: "PATCH",
      path: this.baseUrl + "/current_user/editor_settings",
      data: {},
    });
  }

  cancelSubscription() {
    return this.request({
      method: "DELETE",
      path: this.baseUrl + "/current_user/subscription",
    });
  }
  updateSubscription() {
    return this.request({
      method: "PATCH",
      path: this.baseUrl + "/current_user/subscription",
      data: {},
    });
  }
  createSubscription() {
    return this.request({
      method: "POST",
      path: this.baseUrl + "/current_user/subscription",
      data: {},
    });
  }
  createSse() {
    return this.request({
      method: "POST",
      path: this.baseUrl + "/current_user/sse",
      data: {},
    });
  }
  updateBadge(id: string) {
    return this.request({
      method: "PATCH",
      path: this.baseUrl + "/current_user/badges/" + id,
      data: {},
    });
  }
  getPaymentDetails() {
    return this.request({
      method: "GET",
      path: this.baseUrl + "/current_user/payment_details",
    });
  }
  updatePaymentDetails() {
    return this.request({
      method: "PATCH",
      path: this.baseUrl + "/current_user/payment_details",
      data: {},
    });
  }
  getUploads() {
    return this.request({
      method: "GET",
      path: this.baseUrl + "/current_user/uploads",
    });
  }
  upload() {
    return this.request({
      method: "POST",
      path: this.baseUrl + "/current_user/uploads",
      data: {},
    });
  }
  deleteUpload(id: string) {
    return this.request({
      method: "DELETE",
      path: this.baseUrl + "/current_user/uploads/" + id,
    });
  }
  getFeatureFlags() {
    return this.request({
      method: "GET",
      path: this.baseUrl + "/current_user/feature_flags",
    });
  }
}

export class RESTApi {
  private request: RESTRequest;
  sandboxes: RESTSandboxesApi;
  users: RESTUsersApi;
  project: RESTProjectApi;
  currentUser: RESTCurrentUserApi;
  constructor(request: RESTRequest) {
    this.request = request;
    this.sandboxes = new RESTSandboxesApi(request);
    this.users = new RESTUsersApi(request);
    this.project = new RESTProjectApi(request);
    this.currentUser = new RESTCurrentUserApi(request);
  }

  /**
   * Returns the most recent subscription prices from Stripe
   */
  getPrices() {
    return this.request<{ data: rest.Prices }>({
      method: "GET",
      path: "/prices",
    }).then(({ data }) => data);
  }
  /**
   * @deprecated
   * This should really be using the SSE Sandbox endpoint
   */
  feedback() {}
  /**
   * Get the latest version of an NPM dependency by passing
   * its name and semver. Ex: react@^1.0.1
   */
  getLatestDependencyVersion(dependency: string) {
    return this.request<{ data: rest.DependencyVersion }>({
      method: "GET",
      path: "/dependencies/" + dependency,
    }).then(({ data }) => data);
  }
}
